import {
  action,
  computed,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { SearchableUser } from 'iam/models/SearchableUser'
import Skill from 'iam/models/Skill'
import Workspace from 'iam/models/Workspace'
import MemberStore from 'iam/MemberStore'
import SkillStore from 'iam/SkillStore'
import ModalViewModel from 'infra/ModalViewModel'
import { CursorPageQuery, Id } from 'types/definitions'
import SkillTag from 'iam/models/SkillTag'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'

function getUniqueSkillSet(skills: Skill[]) {
  return new Set(skills.map((skill) => skill.id))
}

export class ManageSkillsListViewModel extends ModalViewModel {
  /**
   * Whether to use the passed-in user as the dialog target.
   */
  @observable
  selectedUser: SearchableUser = SearchableUser.none

  /**
   * The list of available skills.
   */
  @observable
  availableSkills: Skill[] = []

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Workspace = Workspace.none

  /**
   * The currently selected skills.
   */
  @observable
  skills: Set<Id> = new Set()

  /**
   * The currently text to filter skills
   */
  @observable
  searchText: string = ''

  /**
   * A set of the skills ids marks to be removed
   */
  @observable
  removedSkills: Set<Id> = new Set()

  /**
   * The skills pagination  scroller.
   */
  scroller = new InfiniteScrollProvider<Skill>({
    limit: 500, // default page size
    fetchItems: (paging: CursorPageQuery, ..._) => this.fetchSkills(paging),
  })

  constructor(
    private memberStore: MemberStore,
    private skillStore: SkillStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * The list of skills filtered(text search)
   */
  @computed
  get filteredSkills() {
    if (!this.workspace) {
      return []
    }
    if (!this.searchText) {
      return this.availableSkills
    }

    const search = this.searchText.toLowerCase()
    return this.availableSkills.filter(
      (skill) =>
        skill.name.toLowerCase().includes(search) ||
        skill.description?.toLowerCase().includes(search) ||
        skill.tags.some((tag: SkillTag) => {
          return tag.label?.toLowerCase().includes(search)
        }),
    )
  }

  /**
   * A flag to know if all the skills are checked
   */
  @computed
  get allSelected() {
    return (
      this.filteredSkills.length > 0 &&
      this.skills.size === this.filteredSkills.length
    )
  }

  /*
   * Determine whether a workspace is selected
   */
  get isWorkspaceSelected() {
    return this.workspace !== Workspace.none
  }

  /**
   * Set the selected user.
   */
  @action.bound
  setSelectedUser(user: SearchableUser) {
    this.selectedUser = user
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = getUniqueSkillSet(skills)
  }

  /**
   * Update the input options if a workspace is changed.
   */
  @action.bound
  async updateOptions() {
    if (!this.isWorkspaceSelected) {
      return
    }

    await this.skillStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    runInAction(() => {
      this.availableSkills = this.skillStore.forWorkspace(
        Id.assert(this.workspace.id),
      )
    })
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser, workspace: Workspace) {
    this.setWorkspace(workspace)
    this.setSelectedUser(user)
    this.fetchMember(user.publicId)
    this.updateOptions()
    super.show()
  }

  /**
   * Clears manage skills dialog state.
   */
  @action.bound
  reset() {
    this.setSelectedUser(SearchableUser.none)
    this.setAvailableSkills([])
    this.setWorkspace(Workspace.none)
    this.setSkills([])
    this.setSearchText('')
    this.scroller.reset(true)
    this.removedSkills.clear()
  }

  @action.bound
  setSearchText(text: string) {
    this.searchText = text
  }

  /**
   * Update the selected user by adding and/or removing the selected skills.
   */
  @action.bound
  submit = task.resolved(async () => {
    if (
      this.selectedUser === SearchableUser.none ||
      !this.isWorkspaceSelected
    ) {
      return
    }

    try {
      if (this.removedSkills.size > 0) {
        await this.memberStore.removeManySkillsByPublicId(
          this.workspace,
          this.selectedUser.publicId,
          this.scroller.items.filter((skill) =>
            this.removedSkills.has(skill.id),
          ),
        )
      }
      if (this.skills.size > 0) {
        await this.memberStore.addManySkillsByPublicId(
          this.workspace,
          this.selectedUser.publicId,
          this.scroller.items.filter((skill) => this.skills.has(skill.id)),
        )
      }
    } catch (err) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Failed to update skills.')
        .autoDismiss(2500)
        .show()
      return
    }

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success!', 'Skills updated.')
      .autoDismiss(2500)
      .show()

    this.close()
  })

  /**
   * Mark checked/unchecked skill selected
   */
  @action.bound
  toggleSkill(skill: Skill) {
    if (this.skills.has(skill.id)) {
      this.removedSkills.add(skill.id)
      this.skills.delete(skill.id)
    } else {
      this.removedSkills.delete(skill.id)
      this.skills.add(skill.id)
    }
  }

  /**
   * Mark all skills as checked/unchecked
   */
  @action.bound
  selectAllSkills(checked: boolean) {
    if (checked) {
      this.removedSkills.clear()
      this.setSkills(this.filteredSkills)
    } else {
      this.filteredSkills.forEach((skill) => this.removedSkills.add(skill.id))
      this.setSkills([])
    }
  }

  /**
   * Fetch member information to update the member skills
   */
  @action.bound
  async fetchMember(userPublicId: Id) {
    if (!this.isWorkspaceSelected) {
      return
    }
    const member = await this.memberStore.fetchByUserPublicId({
      workspace_id: Id.assert(this.workspace.id),
      user_public_id: Id.assert(userPublicId),
    })

    if (member?.skills.length) {
      runInAction(() => {
        this.skills = getUniqueSkillSet(member.skills)
      })
    }
  }

  /**
   * Fetch available skills in the workspace
   */
  async fetchSkills(params: CursorPageQuery) {
    return await this.skillStore.find({
      ...params,
      workspace_id: Id.assert(this.workspace.id),
    })
  }

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    this.reset()
    super.close()
  }
}
